import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ToggleBtn } from 'components';
import * as S from './ImagePreview.styled';

const mediaUrl = 'https://cdn.appnext.com/banner/';

const getImageSrc = (imageData) => {
  if (!imageData || typeof imageData !== 'string') return '';
  if (imageData.includes('base64') || imageData.startsWith('http')) return imageData;
  return `${mediaUrl}${imageData}`;
};

const formatCompactCount = (value, fallback, suffix = '') => {
  if (value === null || value === undefined || value === '') return fallback;

  const stringValue = String(value).trim();

  if (!stringValue) return fallback;

  const valueWithoutReviewLabel = stringValue.replace(/\s*reviews?$/i, '');
  const normalizedValue = valueWithoutReviewLabel.replace(/,/g, '').replace(/\+$/, '');
  const numericValue = Number(normalizedValue);

  if (Number.isNaN(numericValue)) return valueWithoutReviewLabel;

  const compactValue = [
    { min: 1_000_000_000, label: 'B' },
    { min: 1_000_000, label: 'M' },
    { min: 1_000, label: 'K' },
  ].find(({ min }) => numericValue >= min);
  const suffixValue = numericValue > 0 ? suffix : '';

  if (!compactValue) return `${numericValue}${suffixValue}`;

  const formattedValue = Number((numericValue / compactValue.min).toFixed(1));

  return `${formattedValue}${compactValue.label}${suffixValue}`;
};

export const ImagePreview = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const selectedIcon = useSelector((state) => state.creatives?.selectedCreative?.icon);
  const selectedLandscape = useSelector((state) => state.creatives?.selectedCreative?.image);
  const selectedTitle = useSelector((state) => state.creatives?.selectedCreative?.title);
  const creativeAndTrackingData = useSelector((state) => state.creatives?.creativeAndTrackingData);

  const iconSrc = getImageSrc(selectedIcon);
  const landscapeSrc = getImageSrc(selectedLandscape);

  const appName =
    selectedTitle !== undefined && selectedTitle !== null
      ? selectedTitle
      : creativeAndTrackingData?.app_name_full || creativeAndTrackingData?.app_name || 'App name';
  const appCategory = creativeAndTrackingData?.appCategory || 'Category';
  const appInstallCount = formatCompactCount(creativeAndTrackingData?.appInstallCount, '0', '+');
  const appRating = creativeAndTrackingData?.appRating || '0.0';
  const appReviewCount = formatCompactCount(creativeAndTrackingData?.appReviewCount, '0');

  return (
    <S.Container darkMode={isDarkMode}>
      <S.PhoneFrame>
        <S.SideButton />
        <S.SideButton middle />
        <S.PhoneScreen darkMode={isDarkMode}>
          <S.StatusBar darkMode={isDarkMode}>
            <S.StatusTime darkMode={isDarkMode}>12:23</S.StatusTime>
            <S.StatusIcons darkMode={isDarkMode}>
              <S.SignalIcon darkMode={isDarkMode}>
                <span />
                <span />
                <span />
              </S.SignalIcon>
              <S.BatteryIcon darkMode={isDarkMode} />
              <S.BatteryText>53%</S.BatteryText>
            </S.StatusIcons>
          </S.StatusBar>

          <S.PreviewHeader>
            <S.HeaderTitle darkMode={isDarkMode}>Try new apps</S.HeaderTitle>
            <S.HeaderMeta darkMode={isDarkMode}>
              <span>6 Apps Selected</span>
              <span>Powered by Appnext</span>
            </S.HeaderMeta>
          </S.PreviewHeader>

          <S.SkeletonCard>
            <S.SkeletonFooter>
              <S.SkeletonIcon />
              <S.SkeletonLines>
                <S.SkeletonLine />
                <S.SkeletonLine width='62%' />
                <S.SkeletonLine width='90%' />
              </S.SkeletonLines>
              <S.SkeletonAction />
            </S.SkeletonFooter>
          </S.SkeletonCard>

          <S.CreativeCard>
            <S.LandscapeWrap>
              {landscapeSrc ? (
                <S.LandscapeImage src={landscapeSrc} alt='Selected landscape' />
              ) : (
                <S.LandscapePlaceholder />
              )}
            </S.LandscapeWrap>
            <S.CreativeDetails>
              <S.IconWrap>
                {iconSrc && <S.IconImage src={iconSrc} alt='Selected icon' />}
              </S.IconWrap>
              <S.CreativeBody>
                <S.CreativeTitle>{appName}</S.CreativeTitle>
                <S.CreativeStats>
                  <S.StatBlock>
                    <S.StatHead>
                      {appRating}
                      <S.StarIcon viewBox='0 0 11 10'>
                        <path d='M5.56516 0.0756836L7.03462 3.05314L10.3204 3.5306L7.9428 5.84823L8.50408 9.12077L5.56516 7.57568L2.62623 9.12077L3.18751 5.84823L0.809873 3.5306L4.09569 3.05314L5.56516 0.0756836Z' />
                      </S.StarIcon>
                    </S.StatHead>
                    <S.StatLabel>{appReviewCount} reviews</S.StatLabel>
                  </S.StatBlock>
                  <S.StatDivider />
                  <S.StatBlock>
                    <S.StatHead>{appInstallCount}</S.StatHead>
                    <S.StatLabel>Downloads</S.StatLabel>
                  </S.StatBlock>
                  <S.StatDivider />
                  <S.StatBlock style={{ flexShrink: 1 }}>
                    <S.StatHead>{appCategory}</S.StatHead>
                    <S.StatLabel>Category</S.StatLabel>
                  </S.StatBlock>
                </S.CreativeStats>
              </S.CreativeBody>
              <S.DownloadButton type='button' ariaLabel='Download'>
                <svg viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M23.2003 11.9999H16.8004V18.3159L13.7648 18.3159L20.0001 24.6316L26.2354 18.3159L23.2003 18.3159V11.9999Z'
                  ></path>
                  <rect
                    width='12.7998'
                    height='1.68419'
                    transform='matrix(1 0 0 -1 13.6001 27.9999)'
                  ></rect>
                </svg>
              </S.DownloadButton>
            </S.CreativeDetails>
          </S.CreativeCard>

          <S.SkeletonCard large>
            <S.SelectedDot />
            <S.SkeletonFooter>
              <S.SkeletonIcon />
              <S.SkeletonLines>
                <S.SkeletonLine />
                <S.SkeletonLine width='48%' />
                <S.SkeletonLine width='90%' />
              </S.SkeletonLines>
              <S.SkeletonAction />
            </S.SkeletonFooter>
          </S.SkeletonCard>

          <S.ContinueButton darkMode={isDarkMode} />
        </S.PhoneScreen>
      </S.PhoneFrame>
      <S.ThemeToggle>
        <ToggleBtn
          id='imagePreviewDarkMode'
          isChecked={isDarkMode}
          onChange={setIsDarkMode}
          lableOn='Dark Mode'
          labelOff='Dark Mode'
          isReverse
        />
      </S.ThemeToggle>
    </S.Container>
  );
};
